/**
 * Talking to FiMS (the Finance Management System) from the loader.
 *
 * The chart of accounts lives in the ledger, not in the expenditure system. This
 * screen is the place Finance uploads it, so it posts to FiMS — and, so nothing
 * that works today stops working, it still feeds the expenditure system the six
 * columns that system already reads.
 */

/**
 * The loader is deployed once per company, on its own hostname, so the company
 * is decided by where the page is served from rather than by asking the user —
 * there is no correct answer for them to pick, and picking the wrong one would
 * load one company's chart into the other's books.
 */
export const ENTITIES = {
    legend: {
        id: "610f5aff-8b09-441d-a9d6-d2112aaff429",
        name: "Legend",
    },
    suburban: {
        id: "999e4a86-1f04-4c82-9536-93f622ae87d2",
        name: "Suburban",
    },
};

export const resolveEntity = (hostname = window.location.hostname) => {
    const host = String(hostname).toLowerCase();
    // A settings override wins, for local work against either company's books.
    const override = readSettings()?.defaultStates?.[0]?.entity;
    if (override && ENTITIES[override]) return ENTITIES[override];
    if (host.includes("suburban")) return ENTITIES.suburban;
    if (host.includes("legend")) return ENTITIES.legend;
    return null;
};

export const readSettings = () => {
    try {
        const stored = localStorage.getItem("settings");
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        return null;
    }
};

export const FIMS_LIVE_DEFAULT = "https://financeapi.legend.ng";

export const fimsBaseUrl = () =>
    readSettings()?.defaultStates?.[0]?.fims || FIMS_LIVE_DEFAULT;

/**
 * Template column -> the field FiMS expects. This mirrors TEMPLATE_COLUMNS in
 * FinanceManagementBackend (src/gl/services/accountTemplate.service.ts), which
 * generates the file and exports the same map as HEADER_TO_FIELD.
 *
 * Match on the header, not on column position: Finance sorts and filters the
 * sheet, and a positional reader would silently load the wrong column.
 */
export const HEADER_TO_FIELD = {
    "account no": "code",
    "account name": "name",
    type: "type",
    "normal balance": "balanceDirection",
    "parent code": "parentCode",
    description: "description",
    "opex/capex": "expenditureType",
    category: "category",
    "department/s": "departments",
    "unit/s": "units",
};

/** Headers the older CSV template used, so an existing file still loads. */
export const LEGACY_HEADER_TO_FIELD = {
    "account number": "code",
    "account name": "name",
    "expense category": "category",
    "expense type": "expenditureType",
    department: "departments",
    unit: "units",
};

const normaliseHeader = (header) => String(header ?? "").trim().toLowerCase();

/** Turns one spreadsheet row into a FiMS import payload. */
export const rowToAccount = (row) => {
    const account = {};
    for (const [header, value] of Object.entries(row)) {
        const key = normaliseHeader(header);
        const field = HEADER_TO_FIELD[key] ?? LEGACY_HEADER_TO_FIELD[key];
        if (!field) continue;
        const text = typeof value === "string" ? value.trim() : value;
        if (text === "" || text === null || text === undefined) continue;
        account[field] = typeof text === "string" ? text : String(text);
    }
    if (account.type) account.type = account.type.toUpperCase();
    if (account.balanceDirection) account.balanceDirection = account.balanceDirection.toUpperCase();
    if (account.expenditureType) account.expenditureType = account.expenditureType.toUpperCase();
    return account;
};

/**
 * What the expenditure system reads. Built from the same row, so one upload keeps
 * both systems in step.
 *
 * The first six keys are the ones it has always read. The rest are the columns
 * the template added — the expenditure system stores them now, and ignores any
 * it does not recognise, so sending them is safe against either version.
 *
 * expenditurecategory and expendituretype have been crossed in the past. The
 * expenditure system decides between them by value rather than by name now
 * (OPEX and CAPEX are the only two things one of them can be), so the pairing
 * here is the honest one: the category column goes in the category key.
 */
export const accountToLegacyPayload = (account) => ({
    chartofaccountnumber: account.code ?? "",
    chartofaccountname: account.name ?? "",
    expenditurecategory: account.category ?? "",
    expendituretype: account.expenditureType ?? "",
    department: account.departments ?? "",
    unit: account.units ?? "",
    accounttype: account.type ?? "",
    parentcode: account.parentCode ?? "",
    description: account.description ?? "",
});

/**
 * Problems worth stopping on before anything is sent. FiMS validates the whole
 * upload itself and rejects it atomically; this is only so the obvious mistakes
 * are shown against a row number without a round trip.
 */
export const findLocalProblems = (accounts) => {
    const problems = [];
    const seen = new Map();

    accounts.forEach((account, index) => {
        const row = index + 2; // +1 for the header, +1 because sheets are 1-based
        if (!account.code) problems.push({ row, message: "Account No is missing" });
        // A spreadsheet will happily read 5.1.0000 as a date and hand back
        // 36647.00040509259. It is silent, and it would rewrite the chart's
        // codes, so it is caught here rather than uploaded.
        else if (/^\d+\.\d{6,}$/.test(account.code)) {
            problems.push({
                row,
                message: `Account No ${account.code} was read as a date, not a code. Format that column as Text in the spreadsheet and upload again.`,
            });
        }
        if (!account.name) problems.push({ row, message: "Account Name is missing" });
        // The reason the previous template could not be uploaded at all.
        if (!account.type) {
            problems.push({
                row,
                message: `Type is missing for ${account.code || "this account"} — it decides which statement the account appears on`,
            });
        }
        if (account.code) {
            if (seen.has(account.code)) {
                problems.push({
                    row,
                    message: `Account No ${account.code} is already used on row ${seen.get(account.code)}`,
                });
            } else {
                seen.set(account.code, row);
            }
        }
    });

    const codes = new Set(accounts.map((a) => a.code).filter(Boolean));
    accounts.forEach((account, index) => {
        if (account.parentCode && !codes.has(account.parentCode)) {
            problems.push({
                row: index + 2,
                // Not necessarily wrong: the parent may already be in FiMS from an
                // earlier upload, which is why this is a warning FiMS re-checks.
                message: `Parent Code ${account.parentCode} is not in this file — it must already exist in FiMS`,
                warning: true,
            });
        }
    });

    return problems;
};
